import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { CollapsibleField } from 'components/Wizards/CollapsibleField/CollapsibleField'
import { useField } from 'formik'
import { FeatureFlaggedFieldProps, FieldProps } from './Types'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import Styles from './Field.module.scss'
import { FormikVoiceInput } from './VoiceInput'
import { NumberInput } from './NumberInput'
import { FlaggedFeature } from 'hooks/useFlipperFlag'
import { SourceMaterialInput } from './SourceMaterialInput'
import { NewsletterStyleInput } from 'components/Wizards/Newsletter/NewsletterStyleInput'
import { BooleanInput } from './BooleanInput'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import { BrandKitInput } from './BrandKitInput'
import { BlogLengthSelect } from 'components/Wizards/Wizard/Form/BlogLengthSelect'
import { ReferenceText } from 'components/Wizards/Wizard/Form/ReferenceText'
import { ExternalIntegrationField } from 'components/Wizards/ContentPlanning/ExternalIntegrationField/ExternalIntegrationField'
import { ChannelsList } from './ChannelsList/ChannelsList'
import { StringInput } from './StringInput'
import { ArrayInput } from './ArrayInput'
import { CrossPostAccountsField } from './CrossPostAccountsField'
import { MediaTypeField } from './MediaTypeField/MediaTypeField'
import { Website } from './Website'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BrandKitField } from './BrandKitField/BrandKitField'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

const NAMED_FIELDS = [
  'newsletter_style',
  'brand_visual_kit_id',
  'brand_voice_id',
  'project',
  'source_material_ids',
  'source_visual_element_id',
  'source_visual_element_ids',
  'blog_length',
  'reference_text',
  'external_integration_id',
  'channels',
  'cross_post_accounts',
  'media_type',
  'website',
  'brand_kit_id',
]

const NamedField = ({ name, schema }: Pick<FieldProps, 'name' | 'schema'>) => {
  const { isMobile } = useResponsiveDesign()

  return (
    <>
      {name === 'external_integration_id' && <ExternalIntegrationField schema={schema} />}
      {name === 'brand_visual_kit_id' && <BrandKitInput />}
      {name === 'brand_voice_id' && <FormikVoiceInput />}
      {name === 'source_material_ids' && <SourceMaterialInput />}
      {name === 'newsletter_style' && <NewsletterStyleInput />}
      {name === 'project' && (
        <FilePathFormikField
          placeholder={schema.describe().meta.placeholder}
          locationPickerFullWidth={isMobile}
          inputClassName={cx({
            [Styles.locationPickerField]: isMobile,
            [Styles.inputField]: isMobile,
          })}
          triggerComponent={isMobile ? 'formButton' : 'button'}
        />
      )}
      {name === 'blog_length' && <BlogLengthSelect />}
      {name === 'reference_text' && <ReferenceText />}
      {name === 'channels' && <ChannelsList />}
      {name === 'cross_post_accounts' && <CrossPostAccountsField isDesign={schema.describe().meta.isDesign} />}
      {name === 'media_type' && <MediaTypeField />}
      {name === 'website' && <Website name={name} schema={schema} />}
      {name === 'brand_kit_id' && <BrandKitField />}
    </>
  )
}

const TypedField = ({ name, schema, autoFocus }: FieldProps) => {
  const { tests, type } = schema.describe()

  const min = tests.find(({ name }) => name === 'min')?.params.min
  const max = tests.find(({ name }) => name === 'max')?.params.max

  return (
    <>
      {type === 'string' && <StringInput name={name} schema={schema} autoFocus={autoFocus} />}
      {type === 'array' && <ArrayInput name={name} schema={schema} autoFocus={autoFocus} />}
      {type === 'boolean' && <BooleanInput name={name} />}
      {type === 'number' && <NumberInput name={name} min={min} max={max} autoFocus={autoFocus} />}
    </>
  )
}

const Field = ({ name, schema, autoFocus, rightContent, labelType }: FieldProps) => {
  const {
    meta: { label, placeholder, description, collapsible, hide, copyToClipboard, generate = false },
    nullable,
    optional,
    type,
  } = schema.describe()
  const isRequired = !nullable && !optional
  const [field, meta] = useField(name)
  const { touched, error } = meta
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  if (hide) return null

  const fieldLabel = name === 'brand_kit_id' && isVirtualMarketer ? 'Your Brand Kit' : label

  return (
    <ConditionalWrapper
      key={name}
      condition={collapsible}
      wrapper={(children) => (
        <div className={Styles.optionalField}>
          <CollapsibleField
            buttonLabel={label}
            description={placeholder}
            isExpanded={type === 'array' ? field.value.length > 0 : !!field.value}
            error={touched ? error : undefined}
          >
            {children}
          </CollapsibleField>
        </div>
      )}
      elseWrapper={(children) => {
        return (
          <BaseField
            error={(field.name === 'brand_visual_kit_id' || field.name === 'links') && touched && error}
            name={name}
            copyToClipboard={copyToClipboard}
            key={name}
            label={fieldLabel}
            isRequired={isRequired}
            description={description}
            direction={type === 'boolean' ? 'row' : 'column'}
            withGenerateButton={generate}
            rightContent={rightContent}
            labelType={labelType}
          >
            {children}
          </BaseField>
        )
      }}
    >
      {NAMED_FIELDS.includes(name) && <NamedField name={name} schema={schema} />}
      {!NAMED_FIELDS.includes(name) && <TypedField name={name} schema={schema} autoFocus={autoFocus} />}
    </ConditionalWrapper>
  )
}

const FeatureFlaggedField = observer(({ featureFlags, ...rest }: FeatureFlaggedFieldProps) => {
  const rootStore = useRootStore()

  for (const [flag, shouldBeEnabled] of Object.entries(featureFlags)) {
    if (
      (rootStore.featureFlags.isActive(flag as FlaggedFeature) && !shouldBeEnabled) ||
      (!rootStore.featureFlags.isActive(flag as FlaggedFeature) && shouldBeEnabled)
    ) {
      return null
    }
  }

  return <Field {...rest} />
})

const FieldContainer = (props: FieldProps) => {
  const featureFlags = props.schema.describe().meta.featureFlags

  if (!featureFlags) {
    return <Field {...props} />
  }

  return <FeatureFlaggedField {...props} featureFlags={featureFlags} />
}

export { FieldContainer as Field }
